// Helpers for reasoning about the backend auth session, shared by the two
// places that react to an auth failure: store.apiResult (Java API, which
// signals failure in the JSON body as code 401/403/1004) and the ticketing
// axios instance (NestJS, which uses a real HTTP 401).
import { getCookie } from './cookie'

// Backend session lifetime in days. Must stay in sync with sa-token's `timeout`
// in nftworks-parent (admin/src/main/resources/application.yml: 86400 seconds).
//
// The token cookie used to be written with a 100-year expiry, so from ~24h
// after login onwards the browser kept presenting a token the server had
// already expired. Every page load then fired doomed requests and popped the
// "Currently not logged in" modal instead of simply routing to the login page.
export const SESSION_TTL_DAYS = 1

// Property stamped onto each response with the token that was actually sent.
// Non-enumerable, so it never leaks into JSON.stringify or v-for over a payload.
export const AUTH_TOKEN_FIELD = '__authTokenUsed'

/**
 * Records which token a request was sent with, so a late failure can be matched
 * against the token we currently hold.
 */
export const tagAuthToken = (target, tokenUsed) => {
    if (!target || typeof target !== 'object') {
        return target
    }
    try {
        Object.defineProperty(target, AUTH_TOKEN_FIELD, {
            value: tokenUsed || '',
            enumerable: false,
            configurable: true,
            writable: true,
        })
    } catch (err) {
        // Frozen/sealed payload — the caller just falls back to the old
        // behaviour of treating the failure as current.
    }
    return target
}

/**
 * True when an auth failure belongs to a token we have already replaced, i.e.
 * the response raced a fresh login and arrived late.
 *
 * The app fires several API calls in parallel (App.vue initState, the campaign
 * pages, FrameHead). Once a session expires they all fail. Logging in again
 * while one was still in flight used to be self-defeating: the late failure ran
 * removeCookie('token') and deleted the token the login had just stored,
 * bouncing the user straight back to the login screen. That is the
 * login -> rejected -> login loop seen in the server logs.
 *
 * Unknown provenance (no tag) is treated as current, so any call path that does
 * not tag its requests keeps the previous behaviour.
 */
export const isStaleAuthFailure = (tokenUsedForRequest) => {
    if (!tokenUsedForRequest) {
        return false
    }
    return tokenUsedForRequest !== getCookie('token')
}
